import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import KycStatusChip from "./KycStatusChip";
import {
  fetchKycSubcollection,
  refreshProfileCompliance,
  setKycStatus,
  updateScreening,
  uploadKycEvidence,
  runWalletReconciliationHttp,
  importContractWalletToWalletManagement,
  scanIdentityDocumentForUser,
  saveKycManualFields,
  uploadIdentityDocumentForUser,
  requestKycDocumentUpdate,
  reviewKycDocumentUpdate,
} from "../../../../services/kycService";
import {
  KYC_STATUS,
  SCREENING_STATUS,
  KYC_DOC_TYPES,
  DOCUMENT_UPDATE_REASONS,
  DOCUMENT_UPDATE_STATUS,
  documentUpdateStatusLabel,
  appUserStatusLabel,
  appUserStatusColor,
} from "../../../../Utils/kycConstants";
import { canApproveKyc } from "../../../../Utils/kycCompliance";

const DRAWER_BG = "#14141f";
const SECTION_BG = "rgba(255,255,255,0.04)";
const BORDER = "1px solid rgba(255,255,255,0.1)";
const TEXT_PRIMARY = "rgba(255,255,255,0.92)";
const TEXT_SECONDARY = "rgba(255,255,255,0.65)";

const BTN_OUTLINE = {
  borderColor: "rgba(126,184,255,0.55)",
  color: "#b8d4ff",
  fontWeight: 600,
  fontSize: 13,
  textTransform: "none",
  minHeight: 36,
  "&:hover": { borderColor: "#7eb8ff", bgcolor: "rgba(126,184,255,0.1)" },
};

const BTN_PRIMARY = {
  bgcolor: "#2af598",
  color: "#0a1a12",
  fontWeight: 700,
  fontSize: 13,
  textTransform: "none",
  minHeight: 36,
  "&:hover": { bgcolor: "#3dffb0" },
  "&:disabled": { bgcolor: "rgba(42,245,152,0.25)", color: "rgba(255,255,255,0.4)" },
};

function Section({ title, children, action }) {
  return (
    <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: SECTION_BG, border: BORDER, borderRadius: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: "#2af598", textTransform: "uppercase" }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function FieldRow({ label, value, mono }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 0.25 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: mono ? 12 : 14,
          color: TEXT_PRIMARY,
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: mono ? "break-all" : "normal",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

function isProbablyPdf(url) {
  if (!url) return false;
  try {
    const decoded = decodeURIComponent(String(url)).toLowerCase();
    return decoded.includes(".pdf") || decoded.includes("application/pdf");
  } catch {
    return String(url).toLowerCase().includes(".pdf");
  }
}

function IdentityFilePreview({ url, label }) {
  const [asPdf, setAsPdf] = useState(() => isProbablyPdf(url));

  if (!url) return null;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY, mb: 0.5 }}>{label}</Typography>
      {asPdf ? (
        <Box
          component="iframe"
          src={url}
          title={label}
          sx={{
            width: "100%",
            height: 360,
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            bgcolor: "#0a0a12",
            display: "block",
          }}
        />
      ) : (
        <Box
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          sx={{ display: "block" }}
        >
          <Box
            component="img"
            src={url}
            alt={label}
            onError={() => setAsPdf(true)}
            sx={{
              width: "100%",
              maxHeight: 280,
              objectFit: "contain",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.2)",
              display: "block",
              bgcolor: "rgba(0,0,0,0.25)",
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default function KycClientDetailDrawer({ open, onClose, profile, onUpdated }) {
  const navigate = useNavigate();
  const userId = profile?.user_id || profile?.id;
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [screening, setScreening] = useState(null);
  const [walletRecon, setWalletRecon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importingWallet, setImportingWallet] = useState(false);
  const [scanningId, setScanningId] = useState(false);
  const [uploadingIdentity, setUploadingIdentity] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [error, setError] = useState(null);
  const [scanMessage, setScanMessage] = useState(null);
  const [manualIdNumber, setManualIdNumber] = useState("");
  const [manualExpiry, setManualExpiry] = useState("");
  const [manualDob, setManualDob] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [manualBaseline, setManualBaseline] = useState({
    identity_document_number: "",
    identity_expiry_date: "",
    birth_date: "",
    client_address: "",
  });
  const [screeningStatus, setScreeningStatus] = useState(SCREENING_STATUS.PENDING);
  const [reviewNotes, setReviewNotes] = useState("");
  const [docUpdateReason, setDocUpdateReason] = useState("expired");
  const [docUpdateNote, setDocUpdateNote] = useState("");
  const [docUpdateBusy, setDocUpdateBusy] = useState(false);

  const load = async () => {
    if (!userId || profile?.is_orphan) return;
    setLoading(true);
    try {
      const [docs, conts, scr, wr] = await Promise.all([
        fetchKycSubcollection(userId, "documents"),
        fetchKycSubcollection(userId, "contracts"),
        fetchKycSubcollection(userId, "screening"),
        fetchKycSubcollection(userId, "wallet_reconciliation"),
      ]);
      setDocuments(docs);
      setContracts(conts);
      const prim = scr.find((s) => s.id === "primary") || scr[0];
      setScreening(prim);
      setScreeningStatus(prim?.status || SCREENING_STATUS.PENDING);
      setWalletRecon(wr.find((w) => w.id === "latest") || wr[0]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && profile) load();
  }, [open, profile?.id]);

  useEffect(() => {
    if (!profile) return;
    const idDoc = documents.find((d) => d.id === "users_document_identity");
    const nextNumber =
      profile.identity_document_number ||
      idDoc?.id_number ||
      idDoc?.document_number ||
      profile.users_document_number ||
      "";
    const nextExpiry =
      profile.identity_expiry_date || idDoc?.expiry_date || profile.document_expiry_date || "";
    const nextDob = profile.birth_date || "";
    const primaryContract = contracts.find((c) => c.id === "primary") || contracts[0];
    const nextAddress =
      profile.client_address ||
      profile.contract_summary?.client_address ||
      primaryContract?.client_address?.value ||
      "";
    setManualIdNumber(nextNumber);
    setManualExpiry(nextExpiry);
    setManualDob(nextDob);
    setManualAddress(nextAddress);
    setManualBaseline({
      identity_document_number: nextNumber,
      identity_expiry_date: nextExpiry,
      birth_date: nextDob,
      client_address: nextAddress,
    });
  }, [profile, documents, contracts]);

  const actor = JSON.parse(localStorage.getItem("fiddux_info") || "{}")?.email || "admin";

  const handleApprove = async () => {
    setError(null);
    try {
      const approval = canApproveKyc(profile, documents, screening, contracts[0]);
      if (!approval.allowed) {
        setError(approval.blockers.map((b) => b.label).join("; "));
        return;
      }
      await setKycStatus(userId, KYC_STATUS.APPROVED, actor, reviewNotes);
      await refreshProfileCompliance(userId);
      onUpdated?.();
      onClose();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleScreeningSave = async () => {
    await updateScreening(
      userId,
      { status: screeningStatus, notes: reviewNotes, pep_checked: true, sanctions_checked: true },
      actor
    );
    onUpdated?.();
    load();
  };

  const handleReconcileOne = async () => {
    await runWalletReconciliationHttp(userId);
    load();
    onUpdated?.();
  };

  const handleImportWallet = async () => {
    setError(null);
    setImportingWallet(true);
    try {
      await importContractWalletToWalletManagement(userId, actor);
      await handleReconcileOne();
    } catch (e) {
      setError(e.message || "Failed to import contract wallet");
    } finally {
      setImportingWallet(false);
    }
  };

  const contract = contracts.find((c) => c.id === "primary") || contracts[0];
  const cs = profile?.contract_summary || {};
  const contractWallet = contract?.contract_wallet?.value || cs.contract_wallet || null;
  const docReq = profile?.document_update_request || {};
  const docReqStatus = docReq.status || null;
  const identityDoc = documents.find((d) => d.id === "users_document_identity");
  const identityBackDoc = documents.find((d) => d.id === "users_document_identity_back");
  const identityPhotos = documents.filter(
    (d) =>
      d.storage_url &&
      d.id !== "users_document_identity_back" &&
      (d.id === "users_document_identity" || String(d.id).startsWith("users_document_identity_"))
  );
  const latestIdentity = identityPhotos.slice().sort((a, b) => {
    const ta = Date.parse(a.uploaded_at || "") || 0;
    const tb = Date.parse(b.uploaded_at || "") || 0;
    if (tb !== ta) return tb - ta;
    if (a.id === "users_document_identity") return 1;
    if (b.id === "users_document_identity") return -1;
    return 0;
  })[0];
  const submittedFront = docReq.submitted_document_url || null;
  const submittedBack = docReq.submitted_document_back_url || null;
  const storedFront = latestIdentity?.storage_url || identityDoc?.storage_url || null;
  const storedBack =
    identityBackDoc?.storage_url || identityDoc?.storage_url_back || null;
  // Enviado ou já aprovado: miniatura e os Open ao lado dela usam o arquivo
  // do pedido. Fora isso, a foto mais recente (upload novo) é a que aparece e é escaneada.
  const preferSubmitted =
    docReqStatus === DOCUMENT_UPDATE_STATUS.SUBMITTED ||
    docReqStatus === DOCUMENT_UPDATE_STATUS.APPROVED;
  const identityUrl =
    preferSubmitted && submittedFront
      ? submittedFront
      : storedFront || submittedFront || profile?.users_document_url;
  const identityBackUrl =
    preferSubmitted && submittedBack
      ? submittedBack
      : storedBack || submittedBack || profile?.users_document_back_url;
  const olderIdentityPhotos = latestIdentity
    ? identityPhotos.filter((d) => d.id !== latestIdentity.id)
    : [];
  const idNumber =
    profile?.identity_document_number ||
    identityDoc?.id_number ||
    identityDoc?.document_number ||
    profile?.users_document_number;
  const idExpiry = profile?.identity_expiry_date || identityDoc?.expiry_date;
  const idDob = profile?.birth_date;

  const handleSaveManual = async () => {
    if (!userId || profile?.is_orphan) return;
    setError(null);
    setScanMessage(null);
    const same = (next, prev) => String(next ?? "").trim() === String(prev ?? "").trim();
    const edited = {};
    if (!same(manualIdNumber, manualBaseline.identity_document_number)) {
      edited.identity_document_number = manualIdNumber;
    }
    if (!same(manualExpiry, manualBaseline.identity_expiry_date)) {
      edited.identity_expiry_date = manualExpiry;
    }
    if (!same(manualDob, manualBaseline.birth_date)) {
      edited.birth_date = manualDob;
    }
    if (!same(manualAddress, manualBaseline.client_address)) {
      edited.client_address = manualAddress;
    }
    if (!Object.keys(edited).length) {
      setScanMessage("No changes to save.");
      return;
    }
    setSavingManual(true);
    try {
      const actor = JSON.parse(localStorage.getItem("fiddux_info") || "{}")?.email || "admin";
      await saveKycManualFields(userId, edited, actor);
      setScanMessage("Manual fields saved.");
      load();
      onUpdated?.();
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSavingManual(false);
    }
  };

  const handleUploadIdentity = async (file) => {
    if (!file || !userId || profile?.is_orphan) return;
    setError(null);
    setScanMessage(null);
    setUploadingIdentity(true);
    try {
      const actorEmail = JSON.parse(localStorage.getItem("fiddux_info") || "{}")?.email || "admin";
      await uploadIdentityDocumentForUser(userId, file, actorEmail);
      setScanMessage("Identity document uploaded. You can run OCR or enter fields manually and Save.");
      await load();
      onUpdated?.();
    } catch (e) {
      setError(e.message || "Failed to upload identity document");
    } finally {
      setUploadingIdentity(false);
    }
  };

  const canRequestDoc =
    !profile?.is_orphan &&
    docReqStatus !== DOCUMENT_UPDATE_STATUS.PENDING &&
    docReqStatus !== DOCUMENT_UPDATE_STATUS.SUBMITTED;
  const canReviewDoc = !profile?.is_orphan && docReqStatus === DOCUMENT_UPDATE_STATUS.SUBMITTED;

  const handleRequestDocumentUpdate = async () => {
    if (!userId || !canRequestDoc) return;
    setError(null);
    setScanMessage(null);
    setDocUpdateBusy(true);
    try {
      await requestKycDocumentUpdate(userId, {
        reason: docUpdateReason,
        internalNote: docUpdateNote,
      });
      setScanMessage("Request sent. The client will see a notice on Home and receive an email.");
      setDocUpdateNote("");
      onUpdated?.();
    } catch (e) {
      setError(e.message || e.code || "Failed to request document update");
    } finally {
      setDocUpdateBusy(false);
    }
  };

  const handleReviewDocumentUpdate = async (action) => {
    if (!userId || !canReviewDoc) return;
    setError(null);
    setScanMessage(null);
    setDocUpdateBusy(true);
    try {
      await reviewKycDocumentUpdate(userId, { action, reviewNote: docUpdateNote });
      setScanMessage(
        action === "approve"
          ? "Document approved. The client received a confirmation email."
          : "Document rejected. The Home notice is back and the client was asked to resubmit in the app."
      );
      setDocUpdateNote("");
      onUpdated?.();
      await load();
    } catch (e) {
      setError(e.message || e.code || "Failed to review document");
    } finally {
      setDocUpdateBusy(false);
    }
  };

  const handleScanIdentity = async () => {
    if (!userId) return;
    if (!identityUrl) {
      setError("No identity document on file. Use “Upload identity document” first, then run OCR.");
      return;
    }
    setError(null);
    setScanMessage(null);
    setScanningId(true);
    try {
      const actorEmail = JSON.parse(localStorage.getItem("fiddux_info") || "{}")?.email || "admin";
      const result = await scanIdentityDocumentForUser(userId, identityUrl, actorEmail);
      if (result.applied?.document_number && result.document_number) setManualIdNumber(result.document_number);
      if (result.applied?.expiry_date && result.expiry_date) setManualExpiry(result.expiry_date);
      if (result.applied?.date_of_birth && result.date_of_birth) setManualDob(result.date_of_birth);
      setScanMessage(
        result.message ||
          (result.document_number || result.expiry_date || result.date_of_birth
            ? `Detected: ${result.document_number || "—"} · expiry ${result.expiry_date || "—"} · DOB ${result.date_of_birth || "—"}`
            : "OCR complete — adjust fields below and Save.")
      );
      load();
      onUpdated?.();
    } catch (e) {
      setError(e.message || "Failed to scan identity document");
    } finally {
      setScanningId(false);
    }
  };

  const formatDate = (v) => {
    if (!v) return "—";
    try {
      return new Date(v).toLocaleDateString("en-GB");
    } catch {
      return String(v);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, bgcolor: DRAWER_BG, color: TEXT_PRIMARY } }}
    >
      <Box sx={{ p: 2, pb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
              {profile?.display_name || profile?.folder_name || "Client"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mt: 0.5 }}>
              {profile?.email || "—"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace", mt: 0.25 }}>
              {userId}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: TEXT_PRIMARY }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
          <KycStatusChip status={profile?.kyc_status} />
          {profile?.app_user_status != null && (
            <Chip
              size="small"
              label={`App: ${appUserStatusLabel(profile.app_user_status)}`}
              color={appUserStatusColor(profile.app_user_status)}
              variant="outlined"
            />
          )}
          <Chip size="small" label={`Score ${profile?.completeness_score ?? 0}%`} sx={{ color: TEXT_PRIMARY }} />
          {profile?.is_orphan && <Chip size="small" color="warning" label="Unmatched folder" />}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        {scanMessage && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {scanMessage}
          </Alert>
        )}

        <Section title="Identity document">
          <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 1 }}>
            If there is no ID on file, upload it here first. OCR may fail on scanned PDFs — enter or correct
            fields below, then Save. DOB is stored on the client profile for birthday emails.
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="ID number"
            value={manualIdNumber}
            onChange={(e) => setManualIdNumber(e.target.value)}
            disabled={profile?.is_orphan}
            sx={{ mb: 1, "& .MuiInputBase-root": { color: TEXT_PRIMARY, fontSize: 14 } }}
          />
          <TextField
            size="small"
            fullWidth
            label="Expiry date (YYYY-MM-DD)"
            placeholder="2029-08-12"
            value={manualExpiry}
            onChange={(e) => setManualExpiry(e.target.value)}
            disabled={profile?.is_orphan}
            sx={{ mb: 1, "& .MuiInputBase-root": { color: TEXT_PRIMARY, fontSize: 14 } }}
          />
          <TextField
            size="small"
            fullWidth
            label="Date of birth (YYYY-MM-DD)"
            placeholder="1975-04-23"
            value={manualDob}
            onChange={(e) => setManualDob(e.target.value)}
            disabled={profile?.is_orphan}
            helperText="Saved to client profile (users.birth_date). Auto-filled from OCR when available."
            sx={{
              mb: 1,
              "& .MuiInputBase-root": { color: TEXT_PRIMARY, fontSize: 14 },
              "& .MuiFormHelperText-root": { color: TEXT_SECONDARY, fontSize: 10 },
            }}
          />
          <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
            <Button
              component="label"
              size="small"
              variant="contained"
              disabled={profile?.is_orphan || uploadingIdentity}
              sx={BTN_PRIMARY}
            >
              {uploadingIdentity ? "Uploading…" : "Upload identity document"}
              <input
                type="file"
                hidden
                accept="image/*,.pdf,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) handleUploadIdentity(f);
                }}
              />
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!identityUrl}
              href={identityUrl || undefined}
              target="_blank"
              rel="noreferrer"
              sx={BTN_OUTLINE}
            >
              Open front
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!identityBackUrl}
              href={identityBackUrl || undefined}
              target="_blank"
              rel="noreferrer"
              sx={BTN_OUTLINE}
            >
              Open back
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!identityUrl || profile?.is_orphan || scanningId}
              onClick={handleScanIdentity}
              sx={BTN_PRIMARY}
            >
              {scanningId ? "Scanning…" : "Extract ID / expiry / DOB (OCR)"}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={profile?.is_orphan || savingManual}
              onClick={handleSaveManual}
              sx={{ ...BTN_PRIMARY, bgcolor: "#7eb8ff", color: "#0a1a12", "&:hover": { bgcolor: "#9dcaff" } }}
            >
              {savingManual ? "Saving…" : "Save identity fields"}
            </Button>
          </Box>
          {(idNumber || idExpiry || idDob || identityUrl || identityBackUrl) && (
            <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY, mt: 1 }}>
              On file: {idNumber || "—"} · expiry {idExpiry || "—"} · DOB {idDob || "—"}
              {identityUrl ? " · latest photo" : " · no front"}
              {identityBackUrl ? " · back linked" : ""}
            </Typography>
          )}
          {olderIdentityPhotos.length > 0 && (
            <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY, mt: 0.5 }}>
              OCR uses the latest photo. Earlier photos stay in Documents.
            </Typography>
          )}
          {(identityUrl || identityBackUrl) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
              {identityUrl && <IdentityFilePreview key={`front-${identityUrl}`} url={identityUrl} label="Front" />}
              {identityBackUrl && <IdentityFilePreview key={`back-${identityBackUrl}`} url={identityBackUrl} label="Back" />}
            </Box>
          )}
        </Section>

        <Section title="Document update (app)">
          <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 1 }}>
            Ask this client to send a new ID in the Fiddux app. Does not freeze the account. One client at a
            time — no blast.
          </Typography>
          {docReqStatus && (
            <Chip
              size="small"
              label={documentUpdateStatusLabel(docReqStatus) || docReqStatus}
              sx={{
                mb: 1,
                fontWeight: 700,
                fontSize: 11,
                color:
                  docReqStatus === DOCUMENT_UPDATE_STATUS.SUBMITTED
                    ? "#ffb74d"
                    : docReqStatus === DOCUMENT_UPDATE_STATUS.APPROVED
                      ? "#2af598"
                      : "#b8d4ff",
                border: "1px solid currentColor",
              }}
              variant="outlined"
            />
          )}
          {docReq.reason_label && (
            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 1 }}>
              Reason: {docReq.reason_label}
              {docReq.requested_by ? ` · by ${docReq.requested_by}` : ""}
            </Typography>
          )}
          {(docReq.submitted_document_url || docReq.submitted_document_back_url) && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {docReq.submitted_document_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={docReq.submitted_document_url}
                  target="_blank"
                  rel="noreferrer"
                  sx={BTN_OUTLINE}
                >
                  Open front
                </Button>
              )}
              {docReq.submitted_document_back_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={docReq.submitted_document_back_url}
                  target="_blank"
                  rel="noreferrer"
                  sx={BTN_OUTLINE}
                >
                  Open back
                </Button>
              )}
            </Box>
          )}
          <FormControl size="small" fullWidth sx={{ mb: 1 }} disabled={!canRequestDoc || docUpdateBusy}>
            <InputLabel sx={{ color: TEXT_SECONDARY }}>Reason</InputLabel>
            <Select
              value={docUpdateReason}
              label="Reason"
              onChange={(e) => setDocUpdateReason(e.target.value)}
              sx={{ color: TEXT_PRIMARY, fontSize: 14 }}
            >
              {DOCUMENT_UPDATE_REASONS.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Internal note (optional)"
            value={docUpdateNote}
            onChange={(e) => setDocUpdateNote(e.target.value)}
            disabled={profile?.is_orphan || docUpdateBusy}
            sx={{ mb: 1, "& .MuiInputBase-root": { color: TEXT_PRIMARY, fontSize: 13 } }}
          />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="contained"
              disabled={!canRequestDoc || docUpdateBusy}
              onClick={handleRequestDocumentUpdate}
              sx={BTN_PRIMARY}
            >
              {docUpdateBusy && canRequestDoc ? "Sending…" : "Request document update"}
            </Button>
            {canReviewDoc && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  disabled={docUpdateBusy}
                  onClick={() => handleReviewDocumentUpdate("approve")}
                  sx={BTN_PRIMARY}
                >
                  Approve document
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={docUpdateBusy}
                  onClick={() => handleReviewDocumentUpdate("reject")}
                  sx={BTN_OUTLINE}
                >
                  Reject — ask to resubmit
                </Button>
              </>
            )}
          </Box>
        </Section>

        <Section title="Contract">
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Client address (from contract)"
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            disabled={profile?.is_orphan}
            sx={{ mb: 1, "& .MuiInputBase-root": { color: TEXT_PRIMARY, fontSize: 13 } }}
          />
          <FieldRow label="Signature date" value={formatDate(contract?.signature_date?.value || cs.signature_date)} />
          <FieldRow
            label="Initial amount"
            value={
              contract?.initial_amount?.value != null
                ? `$${Number(contract.initial_amount.value).toLocaleString()}`
                : cs.initial_amount != null
                  ? `$${Number(cs.initial_amount).toLocaleString()}`
                  : "—"
            }
          />
          <FieldRow label="Contract wallet" value={contractWallet} mono />
          <FieldRow
            label="Fiddux wallet (disclosed)"
            value={contract?.fiddux_wallet_disclosed?.value || cs.fiddux_wallet_disclosed}
            mono
          />
          <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
            Source: {contract?.source || cs.source || "folder_scan"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              disabled={!contractWallet}
              onClick={() => navigator.clipboard.writeText(String(contractWallet || ""))}
              sx={BTN_OUTLINE}
            >
              Copy wallet
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={profile?.is_orphan || importingWallet || !contractWallet}
              onClick={handleImportWallet}
              sx={BTN_PRIMARY}
            >
              {importingWallet ? "Importing…" : "Import to Wallet Management"}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={profile?.is_orphan || savingManual}
              onClick={handleSaveManual}
              sx={{ ...BTN_PRIMARY, bgcolor: "#7eb8ff", color: "#0a1a12" }}
            >
              Save address & identity
            </Button>
          </Box>
        </Section>

        <Section
          title="Wallet reconciliation"
          action={
            walletRecon?.reconciliation_status && (
              <Chip size="small" label={walletRecon.reconciliation_status} variant="outlined" sx={{ fontSize: 11 }} />
            )
          }
        >
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
            {walletRecon?.reconciliation_detail || "Not computed yet"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 0.5 }}>
            Paid withdrawals: {walletRecon?.withdrawal_count ?? 0} · Registered:{" "}
            {walletRecon?.registered_wallets?.length ?? 0}
          </Typography>
          <Button
            size="small"
            onClick={handleReconcileOne}
            disabled={!userId || profile?.is_orphan || loading}
            sx={{ mt: 1, ...BTN_OUTLINE }}
          >
            Recompute reconciliation
          </Button>
        </Section>

        <Section title={`Documents (${documents.length})`}>
          <List dense sx={{ maxHeight: 200, overflow: "auto", mb: 1 }}>
            {documents.length === 0 && (
              <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>No documents on file</Typography>
            )}
            {documents.map((d) => (
              <ListItem key={d.id} sx={{ px: 0, alignItems: "flex-start", flexDirection: "column" }}>
                <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
                  <ListItemText
                    primary={`${(d.id === "users_document_identity_back"
                      ? "identity back"
                      : d.id === "users_document_identity" || String(d.id).startsWith("users_document_identity_")
                        ? latestIdentity && d.id === latestIdentity.id && olderIdentityPhotos.length
                          ? "identity front (latest)"
                          : latestIdentity && d.id !== latestIdentity.id && String(d.id).startsWith("users_document_identity")
                            ? "identity front (previous)"
                            : "identity front"
                        : d.doc_type?.replace(/_/g, " ")) || "document"} · ${d.status}`}
                    secondary={
                      d.source_file ||
                      d.label ||
                      (d.source_method === "users_profile" ? "Uploaded at registration" : "")
                    }
                    primaryTypographyProps={{ fontSize: 13, color: TEXT_PRIMARY, textTransform: "capitalize" }}
                    secondaryTypographyProps={{ fontSize: 11, color: TEXT_SECONDARY }}
                  />
                  {d.storage_url && (
                    <Button
                      size="small"
                      href={d.storage_url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ ...BTN_OUTLINE, minWidth: 56 }}
                    >
                      Open
                    </Button>
                  )}
                </Box>
                {d.expiry_date && (
                  <Chip size="small" label={`Expiry ${d.expiry_date}`} variant="outlined" sx={{ mt: 0.5, fontSize: 10 }} />
                )}
              </ListItem>
            ))}
          </List>
          <Button component="label" size="small" variant="outlined" sx={BTN_OUTLINE} disabled={profile?.is_orphan}>
            Upload evidence
            <input
              type="file"
              hidden
              accept="image/*,.pdf,application/pdf"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f || !userId) return;
                setError(null);
                try {
                  await uploadKycEvidence(userId, f, KYC_DOC_TYPES.OTHER, f.name);
                  setScanMessage("Evidence uploaded.");
                  load();
                  onUpdated?.();
                } catch (err) {
                  setError(err.message || "Failed to upload evidence");
                }
              }}
            />
          </Button>
        </Section>

        <Section title="Screening (PEP / sanctions)">
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: TEXT_SECONDARY }}>Status</InputLabel>
            <Select
              value={screeningStatus}
              label="Status"
              onChange={(e) => setScreeningStatus(e.target.value)}
              sx={{ color: TEXT_PRIMARY, fontSize: 14 }}
            >
              {Object.values(SCREENING_STATUS).map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="Review notes"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            sx={{
              mt: 1,
              "& .MuiInputBase-root": { color: TEXT_PRIMARY, fontSize: 14 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
            }}
          />
          <Button
            size="small"
            onClick={handleScreeningSave}
            disabled={profile?.is_orphan}
            sx={{ mt: 1, ...BTN_OUTLINE }}
          >
            Save screening
          </Button>
        </Section>

        {!profile?.is_orphan && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={handleApprove} disabled={loading} sx={{ ...BTN_PRIMARY, minHeight: 42 }}>
              Approve KYC
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate(`/admin/wallet-management?client=${userId}`)}
                sx={BTN_OUTLINE}
              >
                Wallet Management
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() =>
                  navigate("/admin/viewcustomer", {
                    state: { customData: { doc_id: userId }, typeCheck: "Customer" },
                  })
                }
                sx={BTN_OUTLINE}
              >
                View customer
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
